import {
  Context,
  PaginatedResult,
  Id,
  enumGetValues,
  BaseDBService,
  throwObjectAlreadyExists,
  CoreDict,
  BaseDBServiceOpts,
  validateRequiredString,
  validateRequiredNumber,
  throwInvalidOperation,
  runIfDefined,
  throwObjectNotFound,
  trCore,
  createObj,
} from '../../../common'
import {
  AddMunicipalEntityOpts,
  MunicipalEntitiesRepository,
  MunicipalEntitiesService,
  MunicipalEntity,
  MunicipalEntityFindOptions,
  UpdateMunicipalEntityOpts,
} from './municipalEntities'
import { CreateEventMode, ModelEvent, ModelEventsProcessor } from '../../events'
import { ROTransaction, runWithROTransaction, RWTransaction } from '../../../db'
import { Permissions } from '../../authorization'
import { Code } from '../../kladr'
import { FullTextSearchService } from '../../fullTextSearch'
import { isEqual } from 'lodash'

enum EventType {
  MUNICIPAL_ENTITY_ADD = 'MUNICIPAL_ENTITY_ADD',
  MUNICIPAL_ENTITY_UPDATE = 'MUNICIPAL_ENTITY_UPDATE',
  MUNICIPAL_ENTITY_DELETE = 'MUNICIPAL_ENTITY_DELETE',
}

type AddMunicipalEntityPayload = {
  name: string
  index: number
  kladrCode?: Code
}

type UpdateMunicipalEntityPayload = {
  name?: string
  index?: number
  kladrCode?: Code
}

interface DBMunicipalEntitiesServiceOpts extends BaseDBServiceOpts<CoreDict> {
  municipalEntitiesRepository: MunicipalEntitiesRepository
}

export class DBMunicipalEntitiesService
  extends BaseDBService<DBMunicipalEntitiesServiceOpts, CoreDict>
  implements MunicipalEntitiesService
{
  constructor(opts: DBMunicipalEntitiesServiceOpts) {
    super('DbMunicipalEntitiesService', opts)
    this.logger.debug({}, '<2225200b> Initialized')
  }

  private validateName = (name?: string) => validateRequiredString('<2ef1db9c> Name', name?.trim())
  private validateIndexME = (index?: number) => validateRequiredNumber('<3ef2db9c> Index municipal entity', index)
  private validateKladrCode = (code?: string) =>
    validateRequiredString('<c3854960> Kladr code', code, { pattern: /[0-9]{5,8}/ })

  async findAll(ctx: Context, opts?: MunicipalEntityFindOptions): Promise<PaginatedResult<MunicipalEntity>> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.municipalEntitiesRepository.findAll(tx, opts),
    )
  }

  async findById(ctx: Context, id: Id): Promise<MunicipalEntity | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.municipalEntitiesRepository.findById(tx, id),
    )
  }

  async findByIndex(ctx: Context, index: number): Promise<MunicipalEntity | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.municipalEntitiesRepository.findByIndex(tx, index),
    )
  }

  async findByIdNonDeleted(ctx: Context, id: Id): Promise<MunicipalEntity | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, id),
    )
  }

  async add(ctx: Context, opts: AddMunicipalEntityOpts): Promise<Id> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_MANAGE)

    const index = this.validateIndexME(opts.index)

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      ;(await this.opts.municipalEntitiesRepository.findByIndex(tx, index)) &&
        throwObjectAlreadyExists(
          `<1779a489> ${this.translate(ctx, d => d.municipalEntities.alreadyExistsWithSameIndexError, opts.index)}`,
        )

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId: opts.id ?? this.opts.idGenerator.generate(),
        type: EventType.MUNICIPAL_ENTITY_ADD,
        payload: <AddMunicipalEntityPayload>{
          index,
          name: this.validateName(opts.name),
          kladrCode: runIfDefined(opts.kladrCode, c => this.validateKladrCode(c)),
        },
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  async update(ctx: Context, id: Id, opts: UpdateMunicipalEntityOpts): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_MANAGE)
    const index = runIfDefined(opts.index, i => this.validateIndexME(i))

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (index) {
        const meByIndex = await this.opts.municipalEntitiesRepository.findByIndex(tx, index)
        if (meByIndex && meByIndex.id !== id) {
          throwObjectAlreadyExists(
            `<71ad52cc> ${this.translate(ctx, d => d.municipalEntities.alreadyExistsWithSameIndexError, index)}`,
          )
        }
      }

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.MUNICIPAL_ENTITY_UPDATE,
        payload: createObj<UpdateMunicipalEntityPayload>({
          index,
          name: runIfDefined(opts.name, v => this.validateName(v)),
          kladrCode: runIfDefined(opts.kladrCode, v => this.validateKladrCode(v)),
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async delete(ctx: Context, id: Id): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.MUNICIPAL_ENTITIES_MANAGE)
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.MUNICIPAL_ENTITY_DELETE,
      })
    })
    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }
}

function getFieldsToIndex(me: MunicipalEntity): (string | undefined)[] {
  return [me.name, me.index.toString()]
}

export class MunicipalEntitiesModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      municipalEntitiesRepository: MunicipalEntitiesRepository
      fullTextSearchService: FullTextSearchService
    },
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    throwInvalidOperation('<d92af248> Merge not supported')
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.MUNICIPAL_ENTITY_ADD: {
        const p = event.payload as AddMunicipalEntityPayload
        const me: MunicipalEntity = {
          id: event.entityId,
          createdAt: event.createdAt,
          name: p.name,
          index: p.index,
        }
        await this.opts.municipalEntitiesRepository.save(tx, me)
        await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, getFieldsToIndex(me))
        return true
      }
      case EventType.MUNICIPAL_ENTITY_UPDATE: {
        const p = event.payload as UpdateMunicipalEntityPayload
        const me =
          (await this.opts.municipalEntitiesRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(
            trCore(d => d.municipalEntities.notFoundByIdError, event.entityId),
            { uid: '<dfa791b4>' },
          )
        const updatedMe = {
          ...me,
          name: p.name ?? me.name,
          index: p.index ?? me.index,
          kladrCode: p.kladrCode ?? me.kladrCode,
        }
        await this.opts.municipalEntitiesRepository.save(tx, updatedMe)
        const fieldsToIndex = getFieldsToIndex(updatedMe)
        if (!isEqual(fieldsToIndex, getFieldsToIndex(me))) {
          await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, fieldsToIndex)
        }
        return true
      }
      case EventType.MUNICIPAL_ENTITY_DELETE: {
        await this.opts.municipalEntitiesRepository.delete(tx, event.entityId, event.createdAt)
        await this.opts.fullTextSearchService.removeFromIndex(ctx, tx, event.entityId)
        return true
      }
    }
    return false
  }
}
